import React from 'react';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import { PvsCreative, PvsCreativeContent } from 'models/interfaces/index';

const LIST_ALL_CREATIVES_QUERY = gql`
  query ListAllCreatives {
    creatives: pvsCreativeListAll {
      pvs_creative_id
      pvs_campaign_id
      ad_server_parent_id
      name
      platform_id
      click_url
      content_url
      bucket_key
      is_active
      last_sync_date
      is_dirty
      created_date
    }
  }
`;

export const LIST_CREATIVES_BY_CAMPAIGN_QUERY = gql`
  query ListCreativesByCampaign($pvs_campaign_id: String!) {
    creatives: pvsCreativesByCampaignId(pvs_campaign_id: $pvs_campaign_id) {
      pvs_creative_id
      pvs_campaign_id
      ad_server_parent_id
      name
      platform_id
      click_url
      content_url
      bucket_key
      is_active
      last_sync_date
      is_dirty
      created_date
    }
  }
`;

export const GET_CREATIVE_QUERY = gql`
  query GetCreative($pvs_creative_id: String!) {
    creative: pvsCreative(pvs_creative_id: $pvs_creative_id) {
      pvs_creative_id
      pvs_campaign_id
      ad_server_parent_id
      name
      platform_id
      click_url
      content_url
      bucket_key
      is_active
      last_sync_date
      is_dirty
      created_date
    }
  }
`;

export const GET_CREATIVE_UPLOAD_PARAMS_QUERY = gql`
  query GetCreativeUploadParams($key: String!) {
    uploadParams: pvsCreativeUploadResource(key: $key) {
      url
      fields {
        key
        bucket
        X_Amz_Algorithm
        X_Amz_Credential
        X_Amz_Date
        Policy
        X_Amz_Signature
        X_Amz_Security_Token
      }
    }
  }
`;

export const SAVE_CREATIVE_MUTATION = gql`
  mutation SaveCreative($creative: PvsCreativeInput) {
    savePvsCreative(creative: $creative)
  }
`;

export const TOGGLE_ACTIVE_CREATIVE_MUTATION = gql`
  mutation ToggleCreativeIsActive($pvs_creative_id: String, $is_active: Boolean) {
    togglePvsCreativeIsActive(pvs_creative_id: $pvs_creative_id, is_active: $is_active)
  }
`;

export const DELETE_CREATIVE_IMAGE_MUTATION = gql`
  mutation DeleteCreativeImage($key: String, $pvs_creative_id: String) {
    deletePvsCreativeImage(key: $key, pvs_creative_id: $pvs_creative_id)
  }
`;

const GET_CREATIVE_CONTENT_QUERY = gql`
  query GetCreativeContent($pvs_creative_id: String!) {
    creativeContent: pvsCreativeContent(pvs_creative_id: $pvs_creative_id) {
      bytes
    }
  }
`;

interface PvsCreativeContainerRenderProps {
  refetch(): void;
  creative: PvsCreative;
  creativeContent: PvsCreativeContent;
  creatives: PvsCreative[];
}

interface PvsCreativeContainerResults {
  creative: PvsCreative;
  creativeContent: PvsCreativeContent;
  creatives: PvsCreative[];
}

interface PvsCreativeContainerProps {
  pvs_campaign_id?: string;
  pvs_creative_id?: string;
  getContent?: boolean;
  children: (props: PvsCreativeContainerRenderProps) => any;
}

const PvsCreativeContainer: React.FunctionComponent<PvsCreativeContainerProps> = ({
  pvs_campaign_id,
  pvs_creative_id,
  getContent,
  children,
  ...props
}) => {
  let query = LIST_ALL_CREATIVES_QUERY;

  if (pvs_campaign_id) {
    query = LIST_CREATIVES_BY_CAMPAIGN_QUERY;
  } else if (pvs_creative_id && getContent) {
    query = GET_CREATIVE_CONTENT_QUERY;
  } else {
    query = GET_CREATIVE_QUERY;
  }

  return (
    <Query<PvsCreativeContainerResults>
      query={query}
      variables={{ pvs_campaign_id, pvs_creative_id }}
      fetchPolicy={'cache-and-network'}
      errorPolicy="all"
    >
      {({
        data: { creative, creatives, creativeContent } = {
          creative: {} as PvsCreative,
          creativeContent: {} as PvsCreativeContent,
          creatives: [] as PvsCreative[]
        },
        loading,
        error,
        refetch
      }) => {
        if (loading) {
          return null;
        }

        return (
          children &&
          children({
            ...props,
            refetch,
            creative,
            creativeContent,
            creatives
          })
        );
      }}
    </Query>
  );
};

export default PvsCreativeContainer;
